import { ApplicationRef, inject, Injectable } from '@angular/core';
import { setPerfOverlayEnabled } from '@core/perf-overlay/perf-overlay';
import { ThemeService } from '@core/services/theme.service';
import { Theme } from '@core/services/theme.store';
import { MapStyle } from '@features/map/constants/map-style.constants';

import { CHATBOT_MODEL_FLAG_ID, FEATURE_FLAGS_REGISTRY, MAP_STYLE_FLAG_ID, NL_SEARCH_MODEL_FLAG_ID } from '../constants/feature-flags-registry';
import { chatbotDevMode, chatbotDirectMode, chatbotModel, llmDebug, mapStyle, nlSearchModel, perfOverlay, setChatbotDevMode, setChatbotDirectMode, setChatbotModel, setLlmDebug, setMapStyle, setNlSearchModel, setPerfOverlay, setShowWipMarkers, showWipMarkers } from './feature-flags.signals';
import { getRawFlag, setRawFlag } from './feature-flags.storage';

@Injectable({ providedIn: 'root' })
export class FeatureFlagsStore {
    private readonly themeService = inject(ThemeService);
    private readonly appRef = inject(ApplicationRef);

    readonly chatbotDirectMode = chatbotDirectMode;
    readonly chatbotDevMode = chatbotDevMode;
    readonly showWipMarkers = showWipMarkers;
    readonly mapStyle = mapStyle;
    readonly nlSearchModel = nlSearchModel;
    readonly chatbotModel = chatbotModel;

    initialize(): void {
        for (const flag of FEATURE_FLAGS_REGISTRY) {
            if (flag.id === 'dark-mode') {
                continue;
            }

            const stored = getRawFlag(flag.id);

            this.applyFlag(flag.id, stored ?? String(flag.defaultValue));
        }
    }

    toggle(flagId: string): void {
        const newValue = !this.getFlagValue(flagId);

        if (flagId !== 'dark-mode') {
            setRawFlag(flagId, String(newValue));
        }

        this.applyFlag(flagId, String(newValue));
    }

    select(flagId: string, value: string): void {
        setRawFlag(flagId, value);
        this.applyFlag(flagId, value);
    }

    getFlagValue(flagId: string): boolean {
        switch (flagId) {
            case 'chatbot-direct-mode':
                return chatbotDirectMode();
            case 'chatbot-dev-mode':
                return chatbotDevMode();
            case 'show-wip-markers':
                return showWipMarkers();
            case 'llm-debug':
                return llmDebug();
            case 'perf-overlay':
                return perfOverlay();
            case 'dark-mode':
                return this.themeService.isDarkMode();
            default:
                return false;
        }
    }

    getSelectValue(flagId: string): string {
        switch (flagId) {
            case MAP_STYLE_FLAG_ID:
                return mapStyle();
            case NL_SEARCH_MODEL_FLAG_ID:
                return nlSearchModel();
            case CHATBOT_MODEL_FLAG_ID:
                return chatbotModel();
            default:
                return '';
        }
    }

    private applyFlag(flagId: string, value: string): void {
        switch (flagId) {
            case 'chatbot-direct-mode':
                setChatbotDirectMode(value === 'true');
                break;
            case 'chatbot-dev-mode':
                setChatbotDevMode(value === 'true');
                break;
            case 'show-wip-markers':
                setShowWipMarkers(value === 'true');
                break;
            case 'llm-debug':
                setLlmDebug(value === 'true');
                break;
            case 'perf-overlay':
                setPerfOverlay(value === 'true');
                setPerfOverlayEnabled(this.appRef, value === 'true');
                break;
            case 'dark-mode':
                this.themeService.setTheme(value === 'true' ? Theme.Dark : Theme.Light);
                break;
            case MAP_STYLE_FLAG_ID:
                setMapStyle(value as MapStyle);
                break;
            case NL_SEARCH_MODEL_FLAG_ID:
                setNlSearchModel(value);
                break;
            case CHATBOT_MODEL_FLAG_ID:
                setChatbotModel(value);
                break;
        }
    }
}
